import React from "react";
import { Typography, Box, Container } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Parallax } from "react-scroll-parallax";
import { colors } from "../../Theme/theme";
import TeamMember from "./TeamMember";

const teamMembers = [
	{
		name: "David Rhee",
		title: "Attorney at Law",
		sectionTitle: "David Rhee",
		image: "/daveProfileNoBackground.png",
		imagePosition: "center 20%",
		bio: [
			"David Rhee is a dedicated personal injury attorney deeply rooted in the New Mexico community he serves. Born and raised in Albuquerque, David graduated from Albuquerque High School before earning his Bachelor's Degree from The University of New Mexico. His passion for justice and helping others led him to work as a paralegal in Immigration and Personal Injury Law, where he gained invaluable hands-on experience advocating for injured clients and immigrant communities navigating the legal system.",
			"David continued his legal education at The University of New Mexico School of Law, where his commitment to excellence earned him the prestigious 2018 Keller & Keller UNM Law School Challenge Scholarship. After earning his Juris Doctorate and gaining several years of professional experience representing injured clients, David founded David Rhee Injury Law to provide high-quality legal representation to New Mexicans injured by the negligence of others.",
			"As a fluent Spanish speaker, David is uniquely equipped to serve Spanish-speaking and immigrant communities, ensuring every client fully understands their legal options and receives the compassionate, effective representation they deserve. ",
			"Beyond the courtroom, David is a devoted husband and father of two sons, as well as a committed Christian who serves his church in a business owners ministry and men's bible study. He has dedicated significant time to volunteer work both locally and internationally, reflecting his belief in service to others.",
		],
	},
	{
		name: "Erika Lopez",
		title: "Paralegal",
		sectionTitle: "Erika Lopez",
		image: "/erikaNewProfile.png",
		imagePosition: "center 20%",
		bio: [
			"Erika Lopez is a dedicated paralegal with over eight years of direct experience in personal injury law. She brings extensive knowledge of case management, medical records review, and client communication to every file she touches, playing a key role in supporting attorneys and clients through all phases of litigation.",
			"Known for her attention to detail and client-focused approach, Erika is committed to handling each case efficiently and with care, ensuring clients feel informed and supported at every step of their legal journey.",
		],
	},
];

const AboutSection = styled(Box)(({ theme }) => ({
	backgroundColor: "#f5f6f8",
	position: "relative",
	overflow: "hidden",
	paddingTop: theme.spacing(10),
	paddingBottom: theme.spacing(10),
	borderTop: `16px solid ${colors.primary.main}`,
	"&::before": {
		content: '""',
		position: "absolute",
		left: 0,
		top: 0,
		width: "16px",
		height: "100%",
		background: `linear-gradient(to bottom, ${colors.primary.main} 80%, ${colors.accent.main} 100%)`,
	},
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
	color: colors.primary.main,
	fontWeight: 700,
	fontSize: "clamp(2.8rem, 4.5vw, 5rem)",
	letterSpacing: "0.02em",
	textTransform: "none",
	fontFamily: "Georgia, 'Times New Roman', serif",
	lineHeight: 1.2,
	marginBottom: theme.spacing(2),
}));

const GoldDivider = styled(Box)(() => ({
	width: "64px",
	height: "4px",
	background: `linear-gradient(90deg, ${colors.accent.main}, ${colors.accent.light})`,
	marginBottom: "28px",
}));

const ParallaxBackground = styled(Box)(() => ({
	position: "absolute",
	top: 0,
	left: 0,
	right: 0,
	height: "100%",
	backgroundImage: "url(/building2Bg.jpg)",
	backgroundSize: "cover",
	backgroundPosition: "center",
	opacity: 0.1,
	filter: "grayscale(1) contrast(0.9)",
	mixBlendMode: "luminosity",
	maskImage: "linear-gradient(to bottom, transparent, rgba(0,0,0,0.9) 25%, rgba(0,0,0,0.9) 75%, transparent)",
	WebkitMaskImage:
		"linear-gradient(to bottom, transparent, rgba(0,0,0,0.9) 25%, rgba(0,0,0,0.9) 75%, transparent)",
}));

const AboutMD = () => {
	const [ref, inView] = useInView({
		threshold: 0.1,
		triggerOnce: true,
	});

	return (
		<AboutSection id="About" ref={ref}>
			<Parallax speed={-15} style={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}>
				<ParallaxBackground />
			</Parallax>

			<Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
				{/* Section header */}
				<Box sx={{ mb: 8, pl: { xs: 2, md: 2, lg: 4, xl: 8 } }}>
					<motion.div
						initial={{ opacity: 0, y: -20 }}
						animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
						transition={{ duration: 0.8 }}
					>
						<Typography
							sx={{
								fontSize: "0.78rem",
								fontWeight: 700,
								letterSpacing: "0.18em",
								color: colors.primary.main,
								textTransform: "uppercase",
								mb: 2,
								fontFamily: "'Heebo', sans-serif",
							}}
						>
							The Attorneys
						</Typography>
						<SectionTitle>Our Team</SectionTitle>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, scaleX: 0 }}
						animate={inView ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
						transition={{ duration: 0.55, delay: 0.25, ease: "easeOut" }}
						style={{ transformOrigin: "left" }}
					>
						<GoldDivider />
					</motion.div>
				</Box>

				<Box sx={{ px: { xs: 2, md: 2, lg: 4, xl: 8 } }}>
					{teamMembers.map((member, index) => (
						<TeamMember
							key={member.name}
							member={member}
							index={index}
							inView={inView}
							isReversed={index % 2 === 1}
						/>
					))}
				</Box>
			</Container>
		</AboutSection>
	);
};

export default AboutMD;
